import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError, fileUrl, formatDuration } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, Trash2, Copy, GripVertical, Film, Image as ImageIcon, Eye, Shuffle, CheckSquare, Square, ArrowRight, Volume2, VolumeX, Loader2, Move, LayoutGrid, Check } from "lucide-react";
import { toast } from "sonner";

const MOVES = [["none", "None"], ["zoom_in", "Zoom in"], ["zoom_out", "Zoom out"], ["zoom_in_fast", "Bold zoom"], ["pan_right", "Pan right"], ["pan_left", "Pan left"], ["tilt_up", "Tilt up"], ["tilt_down", "Tilt down"], ["random", "Cinematic"]];

export default function OrganiserPanel({ event, goNext }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const dragIdx = useRef(null);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [layoutSpecs, setLayoutSpecs] = useState([]);
  const [layoutType, setLayoutType] = useState(null);
  const [layoutPicks, setLayoutPicks] = useState([]);
  const [layoutCaptions, setLayoutCaptions] = useState({});
  const [creatingLayout, setCreatingLayout] = useState(false);

  useEffect(() => { api.get("/meta").then((r) => setLayoutSpecs(r.data.layouts || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/events/${event.id}/media`); setItems(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, fields) => {
    setItems((its) => its.map((i) => (i.id === id ? { ...i, ...fields } : i)));
    try { await api.patch(`/media/${id}`, fields); } catch (e) { toast.error(apiError(e)); }
  };

  const persistOrder = async (arr) => {
    setItems(arr);
    try { await api.put(`/events/${event.id}/timeline/reorder`, { order: arr.map((i) => i.id) }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const onDrop = (targetIdx) => {
    const from = dragIdx.current;
    if (from === null || from === targetIdx) return;
    const arr = [...items];
    const [moved] = arr.splice(from, 1);
    arr.splice(targetIdx, 0, moved);
    persistOrder(arr);
    dragIdx.current = null;
  };

  const selectAll = async (val) => {
    setItems((its) => its.map((i) => ({ ...i, selected: val })));
    try { await api.post(`/events/${event.id}/media/bulk-select`, { mode: val ? "all" : "none" }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const favouritesOnly = async () => {
    setItems((its) => its.map((i) => ({ ...i, selected: !!i.favourite })));
    try { await api.post(`/events/${event.id}/media/bulk-select`, { mode: "favourites" }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const shuffle = () => {
    const arr = [...items];
    for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [arr[i], arr[j]] = [arr[j], arr[i]]; }
    persistOrder(arr);
    toast.success("Media shuffled");
  };

  const sortBy = (key) => {
    const arr = [...items];
    if (key === "filename") arr.sort((a, b) => (a.original_filename || "").localeCompare(b.original_filename || ""));
    else if (key === "created") arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    persistOrder(arr);
  };

  const duplicate = async (id) => { try { await api.post(`/media/${id}/duplicate`); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { try { await api.delete(`/media/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };

  const openLayout = () => {
    const first = layoutSpecs[0];
    setLayoutType(first ? first.id : null);
    setLayoutPicks([]);
    setLayoutCaptions({});
    setLayoutOpen(true);
  };
  const currentSpec = layoutSpecs.find((l) => l.id === layoutType);
  const togglePick = (id) => {
    setLayoutPicks((p) => {
      if (p.includes(id)) return p.filter((x) => x !== id);
      if (currentSpec && p.length >= currentSpec.count) return p; // cap at required count
      return [...p, id];
    });
  };
  const createLayout = async () => {
    if (!currentSpec || layoutPicks.length < currentSpec.count) return;
    setCreatingLayout(true);
    try {
      const body = { layout: layoutType, photo_ids: layoutPicks };
      if (layoutType === "polaroid_3") body.captions = layoutPicks.map((id) => layoutCaptions[id] || "");
      await api.post(`/events/${event.id}/layouts`, body);
      toast.success(`${currentSpec.name} scene added to your storyboard`);
      setLayoutOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setCreatingLayout(false); }
  };
  const photoItems = items.filter((i) => i.kind === "image");

  const selectedCount = items.filter((i) => i.selected).length;
  const favCount = items.filter((i) => i.favourite).length;
  const flaggedCount = items.filter((i) => i.recommended === false).length;
  const defaultMove = event.design?.movement || "zoom_in";

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
  if (items.length === 0) return <div className="text-center py-20 text-muted-foreground">No media yet. Add photos and videos first.</div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl text-ivory">Review Your Storyboard</h2>
          <p className="text-muted-foreground text-sm mt-1">Drag to reorder, set per-photo movement, and confirm before rendering. Edits save automatically.</p>
        </div>
        <Button data-testid="organise-next-button" onClick={goNext} variant="outline" className="rounded-full border-gold text-gold hover:bg-gold/10">
          Choose a Style <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>

      {/* Approval checklist */}
      <div data-testid="approval-checklist" className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-navy-card border border-navy-border rounded-xl p-3">
          <p className="text-2xl font-semibold tabular-nums text-ivory">{selectedCount}</p><p className="text-xs text-muted-foreground">of {items.length} in the reel</p>
        </div>
        <div className="bg-navy-card border border-navy-border rounded-xl p-3">
          <p className="text-2xl font-semibold tabular-nums text-gold">{favCount}</p><p className="text-xs text-muted-foreground">favourites</p>
        </div>
        <div className="bg-navy-card border border-navy-border rounded-xl p-3">
          <p className={`text-2xl font-semibold tabular-nums ${flaggedCount ? "text-orange-400" : "text-emerald-400"}`}>{flaggedCount}</p><p className="text-xs text-muted-foreground">flagged not-recommended</p>
        </div>
        <div className="bg-navy-card border border-navy-border rounded-xl p-3 flex items-center">
          <p className="text-xs text-muted-foreground">Tip: run <span className="text-gold">Analyse</span> & <span className="text-gold">Best-of</span> in the Add Photos tab to auto-clean the selection.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 bg-navy-card border border-navy-border rounded-xl p-3">
        <Button data-testid="select-all-button" size="sm" variant="ghost" onClick={() => selectAll(true)} className="text-champagne"><CheckSquare className="w-4 h-4 mr-1.5" /> Select all</Button>
        <Button data-testid="clear-selection-button" size="sm" variant="ghost" onClick={() => selectAll(false)} className="text-champagne"><Square className="w-4 h-4 mr-1.5" /> Clear</Button>
        <Button data-testid="favourites-only-button" size="sm" variant="ghost" onClick={favouritesOnly} className="text-champagne"><Star className="w-4 h-4 mr-1.5" /> Use favourites only</Button>
        <div className="w-px h-6 bg-navy-border mx-1" />
        <Button data-testid="shuffle-button" size="sm" variant="ghost" onClick={shuffle} className="text-champagne"><Shuffle className="w-4 h-4 mr-1.5" /> Shuffle</Button>
        <Button data-testid="layout-scene-button" size="sm" variant="ghost" onClick={openLayout} className="text-gold"><LayoutGrid className="w-4 h-4 mr-1.5" /> Layout scene</Button>
        <Select onValueChange={sortBy}>
          <SelectTrigger data-testid="sort-select" className="w-40 h-9 bg-navy-deep border-navy-border"><SelectValue placeholder="Sort by…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="filename">Filename</SelectItem>
            <SelectItem value="created">Upload time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {items.map((it, idx) => (
          <div key={it.id} data-testid={`timeline-item-${it.id}`}
            draggable
            onDragStart={() => (dragIdx.current = idx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(idx)}
            className={`flex gap-4 bg-navy-card border rounded-xl p-3 transition-colors ${it.selected ? "border-gold/40" : "border-navy-border opacity-70"}`}>
            <div className="flex items-center text-muted-foreground cursor-grab active:cursor-grabbing"><GripVertical className="w-5 h-5" /></div>
            <div className="relative w-24 h-24 rounded-lg overflow-hidden shrink-0 bg-navy-deep">
              {it.thumb_path ? <img src={fileUrl(`/media/${it.id}/thumb`)} alt="" className="w-full h-full object-cover" /> :
                <div className="w-full h-full flex items-center justify-center">{it.kind === "video" ? <Film className="w-7 h-7 text-navy-border" /> : <ImageIcon className="w-7 h-7 text-navy-border" />}</div>}
              {it.recommended === false && (
                <span className="absolute top-1 left-1 text-[8px] font-semibold text-white bg-orange-600/90 px-1 py-0.5 rounded">
                  {it.quality?.is_duplicate ? "Dup" : it.quality?.is_blurred ? "Blur" : it.quality?.is_dark ? "Dark" : "Check"}
                </span>
              )}
              {it.is_layout && (
                <span className="absolute top-1 left-1 text-[8px] font-semibold text-navy bg-gold px-1 py-0.5 rounded flex items-center gap-0.5"><LayoutGrid className="w-2.5 h-2.5" /> Layout</span>
              )}
              <button data-testid={`preview-${it.id}`} onClick={() => setPreview(it)} className="absolute inset-0 bg-navy/50 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity">
                <Eye className="w-6 h-6 text-ivory" />
              </button>
            </div>

            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-gold">{it.kind}{it.is_duplicate ? " · copy" : ""}</span>
                <span className="text-xs text-muted-foreground truncate">{it.original_filename}</span>
                {it.kind === "video" && <span className="text-xs text-muted-foreground">· {formatDuration(it.duration)}</span>}
              </div>
              <Input data-testid={`caption-${it.id}`} value={it.caption || ""} placeholder="Add a caption (optional)…"
                onChange={(e) => setItems((its) => its.map((x) => x.id === it.id ? { ...x, caption: e.target.value } : x))}
                onBlur={(e) => patch(it.id, { caption: e.target.value })}
                className="h-8 bg-navy-deep border-navy-border text-sm" />
              <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                {it.kind === "image" ? (
                  <>
                    <label className="flex items-center gap-2">Display <Input data-testid={`duration-${it.id}`} type="number" min="0.5" step="0.5" value={it.image_duration || 3}
                      onChange={(e) => patch(it.id, { image_duration: parseFloat(e.target.value) || 3 })} className="h-7 w-16 bg-navy-deep border-navy-border" /> s</label>
                    <div className="flex items-center gap-1.5">
                      <Move className="w-3.5 h-3.5 text-gold" />
                      <Select value={it.movement || defaultMove} onValueChange={(v) => patch(it.id, { movement: v })}>
                        <SelectTrigger data-testid={`movement-${it.id}`} className="h-7 w-36 bg-navy-deep border-navy-border text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{MOVES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <>
                    <label className="flex items-center gap-1.5">Trim start <Input data-testid={`trim-start-${it.id}`} type="number" min="0" step="0.5" value={it.trim_start || 0}
                      onChange={(e) => patch(it.id, { trim_start: parseFloat(e.target.value) || 0 })} className="h-7 w-16 bg-navy-deep border-navy-border" /></label>
                    <label className="flex items-center gap-1.5">end <Input data-testid={`trim-end-${it.id}`} type="number" min="0" step="0.5" value={it.trim_end ?? it.duration ?? 0}
                      onChange={(e) => patch(it.id, { trim_end: parseFloat(e.target.value) || 0 })} className="h-7 w-16 bg-navy-deep border-navy-border" /></label>
                    <button data-testid={`mute-${it.id}`} onClick={() => patch(it.id, { muted: !it.muted, use_original_audio: it.muted })} className="flex items-center gap-1.5 text-champagne">
                      {it.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />} {it.muted ? "Muted" : "Audio on"}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 shrink-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-muted-foreground">Use</span>
                <Switch data-testid={`select-toggle-${it.id}`} checked={!!it.selected} onCheckedChange={(v) => patch(it.id, { selected: v })} />
              </div>
              <button data-testid={`favourite-${it.id}`} onClick={() => patch(it.id, { favourite: !it.favourite })} className={it.favourite ? "text-gold" : "text-muted-foreground hover:text-gold"}>
                <Star className="w-5 h-5" fill={it.favourite ? "currentColor" : "none"} />
              </button>
              <button data-testid={`duplicate-${it.id}`} onClick={() => duplicate(it.id)} className="text-muted-foreground hover:text-ivory"><Copy className="w-4 h-4" /></button>
              <button data-testid={`delete-timeline-${it.id}`} onClick={() => del(it.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="bg-navy-card border-navy-border max-w-2xl p-2">
          <DialogHeader className="sr-only"><DialogTitle>Media preview</DialogTitle></DialogHeader>
          {preview && (preview.kind === "video" ? (
            <video src={fileUrl(`/media/${preview.id}/file`)} controls autoPlay className="w-full rounded-lg max-h-[70vh]" />
          ) : (
            <img src={fileUrl(`/media/${preview.id}/file`)} alt="" className="w-full rounded-lg max-h-[70vh] object-contain" />
          ))}
        </DialogContent>
      </Dialog>

      {/* Multi-photo layout builder */}
      <Dialog open={layoutOpen} onOpenChange={setLayoutOpen}>
        <DialogContent className="bg-navy-card border-navy-border max-w-3xl">
          <DialogHeader><DialogTitle className="font-display text-2xl text-ivory flex items-center gap-2"><LayoutGrid className="w-5 h-5 text-gold" /> Create a multi-photo layout</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-champagne uppercase tracking-wider mb-2">Choose a layout</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="layout-type-grid">
                {layoutSpecs.map((l) => (
                  <button key={l.id} data-testid={`layout-type-${l.id}`} onClick={() => { setLayoutType(l.id); setLayoutPicks([]); setLayoutCaptions({}); }}
                    className={`rounded-xl border p-3 text-left transition-colors ${layoutType === l.id ? "border-gold bg-gold/10" : "border-navy-border hover:border-gold/50"}`}>
                    <p className="text-ivory text-sm font-medium">{l.name}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{l.blurb}</p>
                    <p className="text-[10px] text-gold mt-1">{l.count} photos</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-champagne uppercase tracking-wider mb-2">
                Pick photos in order {currentSpec ? `(${layoutPicks.length}/${currentSpec.count})` : ""}
              </p>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-64 overflow-y-auto pr-1" data-testid="layout-photo-picker">
                {photoItems.map((p) => {
                  const rank = layoutPicks.indexOf(p.id);
                  return (
                    <button key={p.id} data-testid={`layout-pick-${p.id}`} onClick={() => togglePick(p.id)}
                      className={`relative aspect-square rounded-lg overflow-hidden border ${rank >= 0 ? "border-gold" : "border-navy-border"}`}>
                      {p.thumb_path ? <img src={fileUrl(`/media/${p.id}/thumb`)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-navy-deep" />}
                      {rank >= 0 && <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-gold text-navy text-[11px] font-bold flex items-center justify-center">{rank + 1}</span>}
                    </button>
                  );
                })}
              </div>
              {photoItems.length === 0 && <p className="text-xs text-muted-foreground">Upload some photos first.</p>}
            </div>
            {layoutType === "polaroid_3" && layoutPicks.length > 0 && (
              <div data-testid="polaroid-captions">
                <p className="text-xs text-champagne uppercase tracking-wider mb-2">Handwritten captions (optional)</p>
                <div className="space-y-2">
                  {layoutPicks.map((id, i) => {
                    const p = photoItems.find((x) => x.id === id);
                    return (
                      <div key={id} className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-gold text-navy text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                        {p?.thumb_path && <img src={fileUrl(`/media/${id}/thumb`)} alt="" className="w-8 h-8 rounded object-cover shrink-0" />}
                        <Input data-testid={`caption-input-${id}`} maxLength={40} value={layoutCaptions[id] || ""}
                          onChange={(e) => setLayoutCaptions((c) => ({ ...c, [id]: e.target.value }))}
                          placeholder={`Caption for photo ${i + 1}…`} className="h-9 bg-navy-deep border-navy-border" style={{ fontFamily: "'Caveat', cursive" }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setLayoutOpen(false)} className="border-navy-border">Cancel</Button>
              <Button data-testid="create-layout-button" onClick={createLayout}
                disabled={creatingLayout || !currentSpec || layoutPicks.length < (currentSpec?.count || 99)}
                className="rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold">
                {creatingLayout ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1.5" /> Add layout scene</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
